<template>
  <b-sidebar
    id="add-new-sidebar"
    :visible="isAddNewSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Add New Currency</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form class="p-2" @submit.prevent="handleSubmit(onSubmit)" @reset.prevent="resetForm">
          <!-- Currency Name -->
          <validation-provider #default="validationContext" name="name" rules="required">
            <b-form-group label="Name" label-for="name">
              <b-form-input
                id="name"
                v-model="currencyData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Currency ShortName -->
          <validation-provider #default="validationContext" name="shortName" rules="required">
            <b-form-group label="Short Name" label-for="shortName">
              <b-form-input
                id="shortName"
                v-model="currencyData.shortName"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Currency ShortName -->
          <validation-provider #default="validationContext" name="Rate" rules="required">
            <b-form-group label="Rate" label-for="rate">
              <b-form-input
                id="rate"
                v-model="currencyData.rate"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Currency ShortName -->
          <validation-provider #default="validationContext" name="symbol" rules="required">
            <b-form-group label="Symbol" label-for="rate">
              <b-form-input
                id="symbol"
                v-model="currencyData.symbol"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Symbol Directions -->
          <validation-provider
            #default="validationContext"
            name="Symbol Direction"
            rules="required"
          >
            <b-form-group
              label="Symbol Direction"
              label-for="symbolDirection"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="currencyData.symbolDirection"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="symbolDirections"
                :reduce="(val) => val.value"
                :clearable="true"
                input-id="symbolDirection"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Currency Status -->
          <validation-provider #default="validationContext" name="Is Active" rules="required">
            <b-form-group
              label="Is Active"
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="currencyData.isActive"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="currencyStatuses"
                :reduce="(val) => val.value"
                :clearable="false"
                input-id="type"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              Add
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormSpinbutton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, watch, computed } from "@vue/composition-api";
import { required, alphaNum, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import countries from "@/@fake-db/data/other/countries";
import store from "@/store";

import useCurrenciesList from "./useCurrenciesList";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormSpinbutton,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isAddNewSidebarActive",
    event: "update:is-add-new-sidebar-active",
  },
  props: {
    isAddNewSidebarActive: {
      type: Boolean,
      required: true,
    },
    symbolDirections: {
      type: Array,
      required: true,
    },
    currencyStatuses: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      countries,
    };
  },
  setup(props, { emit }) {
    const { addCurrency } = useCurrenciesList();

    const blankCurrencyData = {
      name: "",
      shortName: "",
      symbol: "",
      symbolDirection: "",
      rate: 1,
      isActive: true,
    };
    const currencyData = ref(JSON.parse(JSON.stringify(blankCurrencyData)));
    const resetCatData = () => {
      currencyData.value = JSON.parse(JSON.stringify(blankCurrencyData));
    };

    const onSubmit = async () => {
      const response = await addCurrency(currencyData.value);
      if (typeof response !== "undefined") {
        emit("refetch-data");
        emit("update:is-add-new-sidebar-active", false);
      }
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetCatData);

    return {
      currencyData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-new-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
